"use client";

import { Copy, ExternalLink, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { CitationNoteEditor } from "@/components/citation-note-editor";
import { useBibStrings } from "@/components/citation-tool/tool-strings";
import { InTextCitationPopover } from "@/components/in-text-citation-popover";
import { Button } from "@/components/ui/button";
import type { SavedCitation } from "@/lib/bibliography";
import { writeToClipboardOrThrow } from "@/lib/clipboard";
import { t } from "@/lib/i18n/interpolate";

interface InspectorProps {
  citation: SavedCitation | null;
  styleName: string;
  onEdit: () => void;
  onDelete: () => void;
  onSaved: () => void;
  onClose?: () => void;
}

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function sourceLink(citation: SavedCitation): string | null {
  if (citation.entry.DOI) {
    return `https://doi.org/${citation.entry.DOI}`;
  }
  return citation.entry.URL || null;
}

/**
 * The detail pane for one reference (Scribbr's slide-over, Zotero's info tab): the
 * formatted entry with a one-click copy, the in-text citation with locator, the
 * source fields, and the annotation. Rendered as a column on wide screens and inside
 * a sheet on narrow ones; the parent decides which.
 */
export function Inspector({
  citation,
  styleName,
  onEdit,
  onDelete,
  onSaved,
  onClose,
}: InspectorProps) {
  const bib = useBibStrings();

  if (!citation) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 py-12 text-center">
        <p className="text-muted-foreground text-sm leading-relaxed">
          {bib.studioInspectorEmpty}
        </p>
      </div>
    );
  }

  const fields: { label: string; value?: string }[] = [
    { label: bib.fieldAuthor, value: citation.entry.author },
    { label: bib.fieldDate, value: citation.entry.issued },
    { label: bib.sitePublication, value: citation.entry.containerTitle },
    { label: bib.fieldPublisher, value: citation.entry.publisher },
    { label: bib.fieldVolume, value: citation.entry.volume },
    { label: bib.fieldIssue, value: citation.entry.issue },
    { label: bib.fieldPages, value: citation.entry.page },
    { label: "DOI", value: citation.entry.DOI },
    { label: "URL", value: citation.entry.URL },
  ].filter((field) => field.value);
  const link = sourceLink(citation);

  async function copyEntry() {
    try {
      await writeToClipboardOrThrow(citation?.citation ?? "");
      toast.success(bib.studioEntryCopied);
    } catch {
      toast.error(bib.copyFailed);
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-3 border-border/70 border-b px-4 py-3">
        <div className="min-w-0">
          <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
            {bib.studioInspectorTitle}
          </p>
          <p className="mt-0.5 truncate text-muted-foreground text-xs">
            {styleName} ·{" "}
            {t(bib.savedOn, { date: formatDate(citation.savedAt) })}
          </p>
        </div>
        {onClose ? (
          <Button
            onClick={onClose}
            size="icon-xs"
            title={bib.close}
            variant="ghost"
          >
            <X className="size-4" />
            <span className="sr-only">{bib.close}</span>
          </Button>
        ) : null}
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto px-4 py-4">
        <div>
          <p className="wrap-break-word whitespace-pre-wrap font-serif text-[15px] text-foreground leading-relaxed">
            {citation.citation}
          </p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <Button onClick={copyEntry} size="sm" variant="outline">
              <Copy className="size-4" />
              {bib.studioCopyEntry}
            </Button>
            <InTextCitationPopover citation={citation} styleName={styleName} />
            {link ? (
              <Button asChild size="sm" variant="ghost">
                <a href={link} rel="noopener noreferrer" target="_blank">
                  <ExternalLink className="size-4" />
                  {citation.entry.DOI ? "DOI" : "URL"}
                </a>
              </Button>
            ) : null}
          </div>
        </div>

        {fields.length > 0 ? (
          <section>
            <h3 className="mb-2 font-medium text-muted-foreground text-xs uppercase tracking-wide">
              {bib.studioSourceDetails}
            </h3>
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
              {fields.map((field) => (
                <div className="contents" key={field.label}>
                  <dt className="text-muted-foreground">{field.label}</dt>
                  <dd className="wrap-break-word min-w-0 text-foreground">
                    {field.value}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ) : null}

        <section>
          <h3 className="mb-2 font-medium text-muted-foreground text-xs uppercase tracking-wide">
            {bib.noteLabel}
          </h3>
          <CitationNoteEditor citation={citation} onSaved={onSaved} />
        </section>
      </div>

      <div className="flex items-center gap-2 border-border/70 border-t px-4 py-3">
        <Button onClick={onEdit} size="sm" variant="outline">
          <Pencil className="size-4" />
          {bib.editTitle}
        </Button>
        <Button
          className="ms-auto text-destructive hover:bg-destructive/10 hover:text-destructive"
          onClick={onDelete}
          size="sm"
          variant="ghost"
        >
          <Trash2 className="size-4" />
          {bib.delete}
        </Button>
      </div>
    </div>
  );
}
